/** @file Shell: top bar, the routed screen, status bar, and the menu and toast layers above everything. */
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import { AgentScreen } from '../screens/AgentScreen';
import { ArtScreenFrame } from '../screens/ArtScreenFrame';
import { PoseScreen } from '../screens/PoseScreen';
import { ProjectsScreen } from '../screens/ProjectsScreen';
import { PuppetsScreen } from '../screens/PuppetsScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import { tileBindings } from '../screens/tile/tileBindings';
import { TileScreen } from '../screens/tile/TileScreen';
import { useTileTabs } from '../screens/tile/useTileTabs';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import { ContextMenuLayer } from './ContextMenuLayer';
import { DialogLayer } from './DialogLayer';
import { MenuBar } from './MenuBar';
import { StatusBar } from './StatusBar';
import { ToastLayer } from './ToastLayer';
import { TopBar } from './TopBar';
import { useApi } from './useApi';
import { useStaleBuild } from './useStaleBuild';
import { useTurntable } from './useTurntable';

/** Every binding table Settings lists (the shell's first), the stored overrides and how to change them. */
export type ShellProps = { bindingTables: Binding[][]; overrides: Binding[]; onOverrides: (overrides: Binding[]) => void };

/** The screen for the current route. */
function RoutedScreen(props: ShellProps): ReactElement {
  const route = useEditor((s) => s.route);
  switch (route.screen) {
    case 'projects': return <ProjectsScreen />;
    case 'puppets': return <PuppetsScreen key={route.projectId} projectId={route.projectId} />;
    case 'settings': return <SettingsScreen {...props} />;
    case 'pose': return <PoseScreen />;
    case 'art': return <ArtScreenFrame />;
    case 'agent': return <AgentScreen />;
    case 'tile': return <TileScreen />;
  }
}

/**
 * The signed-in editor layout (a 26 / 40 / 1fr / 24 px grid: menu bar, top bar, screen, status bar). The Tile tab's
 * key table joins the tables Settings and the Shortcuts dialog list; Tile tabs persist per puppet (`useTileTabs`).
 */
export function Shell(given: ShellProps): ReactElement {
  const store = useEditorStore();
  useTurntable(store);
  useTileTabs(store);
  const build = useStaleBuild(useApi()?.url ?? null);
  const tables = useMemo(() => [...given.bindingTables, tileBindings], [given.bindingTables]);
  const props = { ...given, bindingTables: tables };
  return (
    <div className="p3-app">
      <MenuBar bindingTables={props.bindingTables} overrides={props.overrides} />
      <TopBar />
      <main className="p3-main"><RoutedScreen {...props} /></main>
      <StatusBar serverVersion={build.version} />
      <ContextMenuLayer />
      <DialogLayer bindingTables={props.bindingTables} overrides={props.overrides} />
      <ToastLayer stale={build.stale} />
    </div>
  );
}
